import { Button, Group, Paper, Stack, Text, Transition } from "@mantine/core";
import cx from "clsx";
import { type FC, useEffect, useState } from "react";

import { useExpertUniversityOverrides } from "@/components/workspace/hooks/useExpertUniversityOverrides";
import { FeedbackButton } from "@/header/FeedbackButton/FeedbackButton";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";

import type { EvaluationDetailsBody } from "../api/evaluations/types";

import { EvaluationCalloutBadge } from "./EvaluationCalloutBadge";
import classes from "./EvaluationPage.module.css";

export interface EvaluationCalloutProps {
    activeBehavior?: EvaluationDetailsBody & { category?: string };
    onClickOk: () => void;
    pagination?: React.ReactNode;
    scrollOffset: number;
    transcriptLength: number;
    trainingSessionId?: string;
}

export const EvaluationCallout: FC<EvaluationCalloutProps> = ({
    activeBehavior,
    onClickOk,
    pagination,
    scrollOffset,
    transcriptLength,
    trainingSessionId,
}) => {
    const [opened, setOpened] = useState(false);
    let { feedback, feedbackType } = usePartnerFeatureConfigured();
    const feedbackOverrides = useExpertUniversityOverrides() || undefined;

    useEffect(() => {
        setOpened(true);
    }, []);

    if (feedbackOverrides?.feedback) {
        feedback = feedbackOverrides.feedback;
        feedbackType = feedbackOverrides.feedbackType;
    }

    return (
        <Transition
            mounted={opened}
            transition="fade"
            duration={400}
            timingFunction="ease"
            enterDelay={200}
            exitDelay={300}
        >
            {(transitionStyle) => (
                <Paper
                    shadow="xl"
                    w="100%"
                    bg="var(--mantine-color-dark-9)"
                    bdrs="0.75rem"
                    className={cx(classes.evaluationCallout)}
                    style={{
                        ...transitionStyle,
                        top: scrollOffset,
                    }}
                >
                    <Group py="0.75rem" px="1.25rem" className={classes.calloutHeader}>
                        {activeBehavior?.category && (
                            <Text
                                fw={700}
                                size="md"
                                c="var(--mantine-color-gray-5)"
                                className={classes.calloutHeaderTitle}
                            >
                                {activeBehavior.category}
                            </Text>
                        )}
                        {pagination && (
                            <Group justify="flex-end" flex="1">
                                {pagination}
                            </Group>
                        )}
                    </Group>
                    <Group py="0.75rem" px="1.25rem" gap="0.5rem">
                        <Text size="1.25rem" fw="700" flex="1" className={classes.calloutHeaderTitle}>
                            {activeBehavior?.behavior ?? "Behavior missing from evaluation"}
                        </Text>
                        {activeBehavior?.behaviorPresent !== undefined && (
                            <EvaluationCalloutBadge
                                activeBehavior={activeBehavior}
                                transcriptLength={transcriptLength}
                            />
                        )}
                    </Group>
                    <Stack p="1.25rem" gap="1.75rem">
                        {activeBehavior?.content ? (
                            <Text size="sm" fw="500" lh="150%">
                                {activeBehavior?.content}
                            </Text>
                        ) : (
                            <Text size="sm" fw="500" lh="150%">
                                There is not an associated behavior to review, please press the &ldquo;OK, got it&rdquo;
                                button continue.
                            </Text>
                        )}
                        <Group justify="flex-end">
                            {feedback && feedbackType && (
                                <FeedbackButton
                                    feedbackType={feedbackType}
                                    sessionID={trainingSessionId}
                                    variant="transparent"
                                    isTraining={true}
                                />
                            )}
                            <Button
                                size="xs"
                                color="var(--mantine-color-success-5)"
                                onClick={() => {
                                    setOpened(false);
                                    onClickOk();
                                }}
                            >
                                OK, got it
                            </Button>
                        </Group>
                    </Stack>
                </Paper>
            )}
        </Transition>
    );
};

export default EvaluationCallout;
